// TEN10 (docs/build-briefs/TEN10-software-owner-help.md; DEC-310): the Help section's pure
// half. A guide arrives from the server as a small Markdown subset and becomes a list of
// blocks; the page renders each block with textContent and element creation only, never as
// markup, so a guide cannot inject anything.
//
// The subset: `## ` and `### ` headings, paragraphs, `- ` bullets, pipe tables with a
// `| --- |` separator row, `![alt](name.png)` images followed by an optional `*caption*` line,
// and `**bold**` inside text. The first `# ` line (the title) and the summary paragraph under it
// are not blocks: the list and the guide header show them from the server's own fields.

export interface Span {
  text: string;
  bold: boolean;
  /** HELPDOCS1: a link to a page of the public knowledge base (https://snipsip.com/help/ only), or `guide:<slug>#<section>`,
   * another guide in the same Help panel. Absent for plain text. */
  href?: string;
}

export type HelpBlock =
  /** HELPDOCS1: `id` is the section id a screen opens (`## Title {#id}`); absent when the heading has none. */
  | { kind: "heading"; level: 2 | 3; spans: Span[]; id?: string }
  | { kind: "paragraph"; spans: Span[] }
  /** HELPDOCS1: `ordered` lists number their steps from `start` (the first item's own number). */
  | { kind: "list"; items: Span[][]; ordered?: true; start?: number }
  | { kind: "table"; head: Span[][]; rows: Span[][][] }
  | { kind: "image"; name: string; alt: string; caption: string };

export interface HelpGuideDto {
  slug: string;
  title: string;
  summary: string;
  markdown: string;
}

export interface HelpGuideSummaryDto {
  slug: string;
  title: string;
  summary: string;
}

const LINK =
  /\[([^\]]+)\]\((https:\/\/snipsip\.com\/help\/[A-Za-z0-9/._#-]*|guide:[a-z0-9-]+(?:#[a-z0-9-]+)?)\)/g;

function parseBold(text: string): Span[] {
  const spans: Span[] = [];
  const parts = text.split("**");
  // An odd count of markers leaves the last marker unmatched: fold its tail back in as text.
  const matchedParts = parts.length % 2 === 1 ? parts.length : parts.length - 1;
  for (let i = 0; i < matchedParts; i++) {
    const part = parts[i] ?? "";
    if (part !== "") spans.push({ text: part, bold: i % 2 === 1 });
  }
  if (matchedParts < parts.length) {
    spans.push({ text: `**${parts.slice(matchedParts).join("**")}`, bold: false });
  }
  return spans;
}

/** Splits `**bold**` runs and `[text](https://snipsip.com/help/...)` or `[text](guide:slug#section)` links. An unmatched `**`
 * stays as plain text, and a link to anywhere else stays as plain text, never a link. */
export function parseSpans(text: string): Span[] {
  const spans: Span[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const at = match.index ?? 0;
    spans.push(...parseBold(text.slice(last, at)));
    spans.push({ text: match[1] ?? "", bold: false, href: match[2] ?? "" });
    last = at + match[0].length;
  }
  spans.push(...parseBold(text.slice(last)));
  return spans;
}

/** Plain text of some spans (for tests and accessible names). */
export function spansText(spans: Span[]): string {
  return spans.map((s) => s.text).join("");
}

function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((cell) => cell.trim());
}

const SEPARATOR = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/;
const IMAGE = /^!\[([^\]]*)\]\(([^)]*)\)\s*$/;
const IMAGE_NAME = /^[a-z0-9-]+\.png$/;
const BULLET = /^ {0,3}- /;
const ORDERED = /^(\d{1,2})\. /;
const HEADING_ID = /^(.*?)\s*\{#([a-z0-9]+(?:-[a-z0-9]+)*)\}$/;

function headingBlock(level: 2 | 3, raw: string): HelpBlock {
  const match = HEADING_ID.exec(raw);
  return match === null
    ? { kind: "heading", level, spans: parseSpans(raw) }
    : { kind: "heading", level, spans: parseSpans(match[1] ?? ""), id: match[2] ?? "" };
}

export function parseGuide(markdown: string): HelpBlock[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: HelpBlock[] = [];
  let i = 0;
  // Skip the title and the summary paragraph beneath it.
  if ((lines[0] ?? "").startsWith("# ")) {
    i = 1;
    while (i < lines.length && (lines[i] ?? "").trim() === "") i++;
    while (i < lines.length && (lines[i] ?? "").trim() !== "") i++;
  }
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      i++;
    } else if (line.startsWith("### ")) {
      blocks.push(headingBlock(3, line.slice(4).trim()));
      i++;
    } else if (line.startsWith("## ")) {
      blocks.push(headingBlock(2, line.slice(3).trim()));
      i++;
    } else if (BULLET.test(line)) {
      const items: Span[][] = [];
      while (i < lines.length && BULLET.test(lines[i] ?? "")) {
        items.push(parseSpans((lines[i] ?? "").replace(BULLET, "").trim()));
        i++;
      }
      blocks.push({ kind: "list", items });
    } else if (ORDERED.test(line)) {
      const start = Number(ORDERED.exec(line)?.[1] ?? "1");
      const items: Span[][] = [];
      while (i < lines.length && ORDERED.test(lines[i] ?? "")) {
        items.push(parseSpans((lines[i] ?? "").replace(ORDERED, "").trim()));
        i++;
      }
      blocks.push({ kind: "list", items, ordered: true, start });
    } else if (line.trim().startsWith("|") && SEPARATOR.test((lines[i + 1] ?? "").trim())) {
      const head = splitRow(line).map(parseSpans);
      i += 2;
      const rows: Span[][][] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        const cells = splitRow(lines[i] ?? "").map(parseSpans);
        // A short row is padded and a long one cut, so the table is always rectangular.
        while (cells.length < head.length) cells.push([]);
        rows.push(cells.slice(0, head.length));
        i++;
      }
      blocks.push({ kind: "table", head, rows });
    } else if (IMAGE.test(line.trim())) {
      const match = IMAGE.exec(line.trim());
      const name = match?.[2] ?? "";
      let caption = "";
      const next = (lines[i + 1] ?? "").trim();
      if (/^\*[^*].*\*$/.test(next)) {
        caption = next.slice(1, -1);
        i++;
      }
      i++;
      // A name that is not a plain image file name is dropped, never fetched.
      if (IMAGE_NAME.test(name))
        blocks.push({ kind: "image", name, alt: match?.[1] ?? "", caption });
    } else {
      const paragraph: string[] = [];
      while (
        i < lines.length &&
        (lines[i] ?? "").trim() !== "" &&
        !/^(#{2,3} |- |\d{1,2}\. |!\[)/.test(lines[i] ?? "")
      ) {
        paragraph.push((lines[i] ?? "").trim());
        i++;
      }
      blocks.push({ kind: "paragraph", spans: parseSpans(paragraph.join(" ")) });
    }
  }
  return blocks;
}

/** True when a reply body has the list shape. */
export function isGuideList(body: unknown): body is { guides: HelpGuideSummaryDto[] } {
  if (typeof body !== "object" || body === null) return false;
  const guides = (body as { guides?: unknown }).guides;
  return (
    Array.isArray(guides) &&
    guides.every(
      (g) =>
        typeof g === "object" &&
        g !== null &&
        typeof (g as HelpGuideSummaryDto).slug === "string" &&
        typeof (g as HelpGuideSummaryDto).title === "string" &&
        typeof (g as HelpGuideSummaryDto).summary === "string",
    )
  );
}

export function isGuideReply(body: unknown): body is { guide: HelpGuideDto } {
  if (typeof body !== "object" || body === null) return false;
  const guide = (body as { guide?: unknown }).guide;
  return (
    typeof guide === "object" &&
    guide !== null &&
    typeof (guide as HelpGuideDto).title === "string" &&
    typeof (guide as HelpGuideDto).markdown === "string"
  );
}

export const HELP_LIST_FAILED = "The guides could not be loaded. Try again.";
export const HELP_GUIDE_FAILED = "That guide could not be loaded. Try again.";
export const HELP_IMAGE_FAILED = "This picture could not be loaded.";

/** UX104: the console screen each guide is about. The guide view offers a button to it. A slug not listed gets no
 * button. The label is the screen's own name in the left navigation. */
export type HelpScreen = "customers" | "onboard";
export const HELP_GUIDE_SCREENS: Readonly<Record<string, { view: HelpScreen; label: string }>> = {
  "onboard-a-new-customer": { view: "onboard", label: "Onboard a new customer" },
  "edit-a-customers-legal-details": { view: "customers", label: "Customers" },
  "link-a-franchisee-to-a-franchisor": { view: "customers", label: "Customers" },
  "create-a-franchisor-account": { view: "customers", label: "Customers" },
};
